<template>
    <div>
        <div v-if="loadingGetData">
            <div class="flex justify-center items-center gap-2 text-gray-500 min-h-[200px]">
                <i class="fas fa-spinner fa-spin"></i> Chargement des données...
            </div>
        </div>
        <div v-if="!loadingGetData" class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-2">

                <!-- Date -->
                <div class="flex items-center gap-2.5 px-3.5 py-2.5 bg-white border shadow-sm rounded transition-colors">
                    <div class="flex flex-col gap-px overflow-hidden">
                        <span class="text-[9px] font-medium text-slate-400 uppercase">Date de l'intervention</span>
                        <span class="text-sm text-slate-800 truncate font-semibold">
                            {{ value.date_intervention | momentDate }} <i class="fas fa-arrow-right"></i> {{ crenauLabels[value.moment_day] }}
                        </span>
                    </div>
                </div>

                <!-- Technicien -->
                <div class="flex items-center gap-2.5 px-3.5 py-2.5 bg-white border shadow-sm rounded transition-colors">
                    <div class="flex flex-col gap-px overflow-hidden">
                        <span class="text-[9px] font-medium text-slate-400 uppercase">Technicien assigné</span>
                        <span class="text-sm text-slate-800 truncate font-semibold">{{ selectedTechnicien?.name || '-' }}</span>
                    </div>
                </div>
            </div>

            <!-- ─── RECHERCHE CLIENT ─────────────────────────────────────────────── -->
            <div>
                <label class="text-sm font-bold text-gray-900">Client</label>
                <div class="flex gap-2 mt-1">
                    <!-- Champ de recherche avec autocomplétion -->
                    <div class="relative flex-1">
                        <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
                        <input
                            type="text"
                            v-model="clientSearch"
                            placeholder="Rechercher un propriétaire.."
                            class="pl-9 w-full rounded-lg border border-gray-300 focus:outline-none text-sm py-2"
                            @focus="showSuggestions = true"
                            @blur="hideSuggestionsDelayed"
                        />

                        <!-- Dropdown suggestions -->
                        <div v-if="showSuggestions" class="absolute w-full bg-white border border-gray-200 rounded-lg mt-1 shadow-lg z-10">
                            <div v-if="loadingSearchClient" class="px-4 py-2 text-sm text-gray-500">
                                <i class="fas fa-spinner fa-spin"></i> Recherche en cours...
                            </div>
                            <div v-else>
                                <ul v-if="filteredClients.length" class="max-h-52 overflow-y-auto">
                                    <li
                                        v-for="client in filteredClients"
                                        :key="client.id"
                                        @mousedown.prevent="selectClient(client)"
                                        class="px-4 py-2 hover:bg-red-100 cursor-pointer text-sm"
                                    >
                                        <div>
                                            <span class="font-medium">{{ client.prenom }} {{ client.nom }}</span>
                                            <span v-if="client.ville" class="text-gray-500 text-xs"> - {{ client.code_postal }} {{ client.ville }}</span>
                                        </div>
                                        <div>
                                            <span v-if="client.email" class="text-gray-500 text-xs">{{ client.email }}</span>
                                            <span v-if="client.telephone" class="text-gray-500 text-xs"> - {{ client.telephone }}</span>
                                        </div>
                                    </li>
                                </ul>

                                <!-- Bouton création rapide client -->
                                <div :class="filteredClients.length ? 'border-t' : ''">
                                    <button
                                        @click="openNewClientModal"
                                        class="px-4 py-4 text-sm text-blue-500 hover:bg-blue-50 w-full text-left flex items-center gap-2"
                                    >
                                        <i class="fas fa-plus mr-1"></i> Nouveau client
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Bouton ajout client direct -->
                    <button
                        @click="$modal.show('modal-add-client-intervention')"
                        class="bg-slate-200 hover:bg-slate-300 px-3 py-3 rounded-lg flex items-center gap-2 text-xs"
                    >
                        <i class="fas fa-plus"></i>
                    </button>
                </div>
            </div>

            <!-- ─── ADRESSE DE L'INTERVENTION ────────────────────────────────────── -->
            <div v-if="dataModel.client_id">
                <label class="text-sm font-bold text-gray-900">Adresse de l'intervention</label>

                <!-- Aucune adresse → formulaire inline -->
                <template v-if="adresses.length === 0">
                    <div class="bg-slate-100 p-4 border rounded mt-1">
                        <adresse-form v-model="newAdresse" :simple="true" />
                        <div class="flex justify-center mt-4">
                            <button
                                :disabled="loadingSaveAdresse"
                                @click="saveAdresse"
                                class="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg shadow transition flex items-center gap-2"
                            >
                                <i v-if="loadingSaveAdresse" class="fas fa-spinner fa-spin"></i>
                                <template v-else><i class="fas fa-check"></i> Enregistrer l'adresse</template>
                            </button>
                        </div>
                    </div>
                </template>

                <!-- Adresses existantes → select + bouton ajout -->
                <template v-if="adresses.length > 0">
                    <div class="flex gap-2 mt-1">
                        <div class="relative mt-1 grow">
                            <i class="fas fa-list absolute left-3 top-3 text-gray-400"></i>
                            <select
                                v-model="dataModel.adresse_id"
                                @change="onAdresseChange"
                                class="pl-9 w-full rounded-lg border border-gray-300 focus:outline-none text-sm py-2"
                            >
                                <option value="">Sélectionner une adresse</option>
                                <option v-for="adresse in adresses" :key="adresse.id" :value="adresse.id">
                                    {{ adresse.adresse1 }} {{ adresse.code_postal }} {{ adresse.ville }}
                                </option>
                            </select>
                        </div>
                        <button
                            @click="openNewAdresseModal"
                            class="bg-slate-200 hover:bg-slate-300 px-3 py-1 rounded-lg flex items-center gap-2 text-xs"
                        >
                            <i class="fas fa-plus"></i>
                        </button>
                    </div>
                </template>
            </div>

            <!-- ─── CHANTIERS EXISTANTS ───────────────────────────────────────────── -->
            <!--
                Visible uniquement si withChantiers est true.
                Sans la prop, on considère implicitement un nouveau chantier
                (chantierSelected = true, dataModel.chantier_id = null).
            -->
            <div v-if="withChantiers && chantiers.length > 0">
                <label class="text-sm font-bold text-gray-900">Attacher à un chantier existant</label>
                <div class="mt-2 space-y-2">

                    <!-- Option "Nouveau chantier" -->
                    <div
                        @click="dataModel.chantier_id = null; $forceUpdate()"
                        class="rounded-lg p-4 cursor-pointer transition hover:shadow-md flex items-center"
                        :class="!dataModel.chantier_id ? 'border-2 border-red-600 bg-red-50' : 'border border-gray-300 hover:border-red-400'"
                    >
                        <span class="text-sm"><i class="fas fa-plus mr-1 text-xs"></i> Nouveau chantier</span>
                    </div>

                    <!-- Chantiers existants -->
                    <div
                        v-for="chantier in chantiers"
                        :key="chantier.id"
                        @click="dataModel.chantier_id = chantier.id; $forceUpdate()"
                        class="rounded-lg p-4 cursor-pointer transition hover:shadow-md"
                        :class="dataModel.chantier_id === chantier.id ? 'border-2 border-red-600 bg-red-50' : 'border border-gray-300 hover:border-red-400'"
                    >
                        <ul class="list-disc ml-4 pr-4 w-full space-y-2">
                            <li v-for="(intervention, index) in chantier.interventions" :key="index" class="text-xs font-light">
                                <div class="flex items-center gap-2 justify-between">
                                    <div>
                                        <div class="font-bold">{{ intervention.reference }}</div>
                                        {{ intervention?.intervention_type?.nom || 'Intervention' }}
                                    </div>
                                    <div class="border-b border-dashed border-slate-400 grow"></div>
                                    <div class="text-slate-400">{{ intervention?.statut?.libelle || 'Inconnu' }}</div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- ─── APPAREIL ──────────────────────────────────────────────────────── -->
            <div v-if="dataModel.adresse_id && chantierSelected">
                <label class="text-sm font-bold text-gray-900">Appareil</label>

                <p v-if="!dataModel.client_id" class="text-xs text-gray-500 mt-1">
                    Veuillez d'abord sélectionner un client.
                </p>

                <template v-else>
                    <!-- Chargement -->
                    <div v-if="loadingAppareil" class="py-2">
                        <i class="fas fa-spinner fa-spin"></i>
                    </div>

                    <template v-else>
                        <!-- Aucun appareil → formulaire inline -->
                        <div v-if="appareils.length === 0" class="bg-slate-100 p-4 border rounded mt-1">
                            <appareil-form v-model="newAppareil" :simple="true" />
                            <div class="flex justify-center mt-6">
                                <button
                                    :disabled="loadingAddAppareil"
                                    @click="saveAppareil"
                                    class="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg shadow transition flex items-center gap-2"
                                >
                                    <i v-if="loadingAddAppareil" class="fas fa-spinner fa-spin"></i>
                                    <template v-else><i class="fas fa-check"></i> Enregistrer l'appareil</template>
                                </button>
                            </div>
                        </div>

                        <!-- Appareils existants → select + bouton ajout -->
                        <div v-else class="flex gap-2 mt-1">
                            <div class="relative mt-1 grow">
                                <i class="fas fa-fire-flame-simple absolute left-3 top-3 text-gray-400"></i>
                                <select
                                    v-model="dataModel.client_appareil_id"
                                    @change="$forceUpdate(); loadTypesIntervention()"
                                    class="pl-9 w-full rounded-lg border border-gray-300 focus:outline-none text-sm py-2"
                                >
                                    <option value="">Sélectionner un appareil</option>
                                    <option v-for="appareil in appareils" :key="appareil.id" :value="appareil.id">
                                        [Ref: {{ appareil.id }}]
                                        {{ appareil.app_type ? appareil.app_type.label : appareil.type }}
                                        <span v-if="appareil.marque"> > {{ appareil.marque }}</span>
                                        <span v-if="appareil.model"> > {{ appareil.model }}</span>
                                    </option>
                                </select>
                            </div>
                            <button
                                @click="$modal.show('modal-add-appareil-intervention')"
                                class="bg-slate-200 hover:bg-slate-300 px-3 py-1 rounded-lg flex items-center gap-2 text-xs"
                            >
                                <i class="fas fa-plus"></i>
                            </button>
                        </div>
                    </template>
                </template>
            </div>

            <!-- ─── TYPE D'INTERVENTION ───────────────────────────────────────────── -->
            <div v-if="dataModel.client_appareil_id">
                <label class="text-sm font-bold text-gray-900">Type d'intervention</label>

                <div v-if="loadingInterventionTypes" class="py-2">
                    <i class="fas fa-spinner fa-spin"></i>
                </div>

                <div v-else class="relative mt-2">
                    <!-- Sélection de la catégorie logique (entretien, dépannage, etc.) -->
                    <div class="grid grid-cols-2 md:grid-cols-2 gap-3">
                        <div
                            v-for="group in groupedInterventionTypes"
                            :key="group.key"
                            @click="selectTypeIntervention(group.key)"
                            class="border rounded-lg p-4 cursor-pointer transition hover:shadow-md"
                            :class="filterTypeIntervention === group.key ? 'border-red-600 bg-red-50' : 'border-gray-300 hover:border-red-400'"
                        >
                            <div class="flex items-center gap-3">
                                <i class="fas fa-list text-gray-400"></i>
                                <span class="font-medium text-sm">{{ group.libelle }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Sélection du type précis (si plusieurs choix disponibles) -->
                    <div v-if="typesFiltered.length > 1" class="pt-4">
                        <label class="text-sm font-bold text-gray-900">Travaux à réaliser</label>
                        <div class="mt-2">
                            <!-- Type déjà sélectionné → affichage avec bouton modifier -->
                            <div v-if="dataModel.type_id" class="border-2 border-red-600 bg-red-50 rounded-lg p-4">
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center gap-3">
                                        <i class="fas fa-wrench text-gray-400"></i>
                                        <span class="font-medium text-sm">{{ selectedTypeName }}</span>
                                    </div>
                                    <button
                                        @click="dataModel.type_id = null; $forceUpdate()"
                                        class="text-red-600 hover:text-red-700 text-sm font-medium"
                                    >
                                        <i class="fas fa-edit mr-1"></i> Modifier
                                    </button>
                                </div>
                            </div>

                            <!-- Grille de sélection du type -->
                            <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div
                                    v-for="type in typesFiltered"
                                    :key="type.id"
                                    @click="selectDureeIntervention(type.id)"
                                    class="border rounded-lg p-4 cursor-pointer transition hover:shadow-md border-gray-300 hover:border-red-400"
                                >
                                    <div class="flex items-center gap-3">
                                        <i class="fas fa-wrench text-gray-400"></i>
                                        <span class="font-medium text-sm">{{ type.nom }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ─── DURÉE DE L'INTERVENTION ───────────────────────────────────────── -->
            <div v-if="dataModel.type_id">
                <label class="text-sm font-bold text-gray-900">Durée estimée de l'intervention</label>
                <div class="mt-2 grid grid-cols-2 gap-3">
                    <select v-model="dureeHeures" class="w-full rounded-lg border border-gray-300 text-sm py-2 px-2 focus:outline-none">
                        <option :value="0">0 h</option>
                        <option v-for="h in 12" :key="h" :value="h">{{ h }} h</option>
                    </select>
                    <select v-model="dureeMinutes" class="w-full rounded-lg border border-gray-300 text-sm py-2 px-2 focus:outline-none">
                        <option v-for="m in minutesOptions" :key="m" :value="m">{{ m }} min</option>
                    </select>
                </div>
            </div>

            <!-- ─── STATUT ─────────────────────────────────────────────────────────
                 Visible uniquement quand durée + type + technicien sont renseignés
            ──────────────────────────────────────────────────────────────────────── -->
            <div v-if="isStatutVisible">
                <label class="text-sm font-bold text-gray-900">Statut</label>
                <div class="relative mt-1">
                    <i class="fas fa-flag absolute left-3 top-3 text-gray-400"></i>
                    <select
                        v-model="dataModel.statut_id"
                        class="pl-9 w-full rounded-lg border border-gray-300 focus:outline-none text-sm py-2"
                    >
                        <option v-for="s in statuts" :key="s.id" :value="s.id">{{ s.libelle }}</option>
                    </select>
                </div>
            </div>

            <!-- ─── NOTES INTERNES ─────────────────────────────────────────────────
                 Visible quand le statut est sélectionné
            ──────────────────────────────────────────────────────────────────────── -->
            <div v-if="dataModel.type_id && dataModel.statut_id" class="md:col-span-2">
                <label class="text-sm font-bold text-gray-900">Notes internes concernant l'intervention</label>
                <textarea
                    v-model="dataModel.notes_internes"
                    rows="3"
                    placeholder="Notes techniques, remarques, suivi client..."
                    class="w-full rounded-lg border border-gray-300 focus:outline-none text-sm p-3"
                ></textarea>
            </div>
        </div>

        <!-- ══════════════════════════════════════════════════════════════════════ -->
        <!--  MODALES                                                              -->
        <!-- ══════════════════════════════════════════════════════════════════════ -->

        <!-- Modale : Ajout client -->
        <modal name="modal-add-client-intervention" height="auto" :scrollable="true" :adaptive="true">
            <div class="p-10 overflow-y-auto max-h-[100vh]" style="z-index: 999 !important;">
                <div class="flex items-center justify-between mb-4 pb-4 border-b text-2xl">
                    <h2 class="font-bold">Client</h2>
                    <button @click="$modal.hide('modal-add-client-intervention')">
                        <i class="fas fa-times text-red-500"></i>
                    </button>
                </div>

                <client-form v-model="newClient" />

                <div class="flex justify-center mt-6">
                    <button
                        :disabled="loadingCreateClient"
                        @click="saveClient"
                        class="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg shadow transition flex items-center gap-2"
                    >
                        <i v-if="loadingCreateClient" class="fas fa-spinner fa-spin"></i>
                        <template v-else><i class="fas fa-check"></i> Enregistrer</template>
                    </button>
                </div>
            </div>
        </modal>

        <!-- Modale : Ajout appareil -->
        <modal name="modal-add-appareil-intervention" height="auto" :scrollable="true" :adaptive="true">
            <div class="p-10 overflow-y-auto max-h-[100vh]" style="z-index: 999 !important;">
                <div class="flex items-center justify-between mb-4 pb-4 border-b text-2xl">
                    <h2 class="font-bold">Ajouter un appareil</h2>
                    <button @click="$modal.hide('modal-add-appareil-intervention')">
                        <i class="fas fa-times text-red-500"></i>
                    </button>
                </div>
                <appareil-form v-model="newAppareil" :simple="true" />
                <div class="flex justify-center mt-6">
                    <button
                        :disabled="loadingCreateAppareil"
                        @click="saveAppareil"
                        class="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg shadow transition flex items-center gap-2"
                    >
                        <i v-if="loadingCreateAppareil" class="fas fa-spinner fa-spin"></i>
                        <template v-else><i class="fas fa-check"></i> Enregistrer</template>
                    </button>
                </div>
            </div>
        </modal>

        <!-- Modale : Ajout adresse -->
        <modal name="modal-add-adresse-intervention" height="auto" :scrollable="true" :adaptive="true">
            <div class="p-10 overflow-y-auto max-h-[100vh]" style="z-index: 999 !important;">
                <div class="flex items-center justify-between mb-4 pb-4 border-b text-2xl">
                    <h2 class="font-bold">Adresse</h2>
                    <button @click="$modal.hide('modal-add-adresse-intervention')">
                        <i class="fas fa-times text-red-500"></i>
                    </button>
                </div>
                <adresse-form v-model="newAdresse" :simple="true" />
                <div class="flex justify-center mt-6">
                    <button
                        :disabled="loadingCreateAdresse"
                        @click="saveAdresse"
                        class="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg shadow transition flex items-center gap-2"
                    >
                        <i v-if="loadingCreateAdresse" class="fas fa-spinner fa-spin"></i>
                        <template v-else><i class="fas fa-check"></i> Enregistrer</template>
                    </button>
                </div>
            </div>
        </modal>
    </div>
</template>

<script>
import moment from 'moment'
import interventionTypeMappingConfig from './../../../config/InterventionTypeMapping.js'
import ClientForm   from '../../Client/Components/ClientForm.vue'
import AppareilForm from '../../Client/Components/AppareilForm.vue'
import AdresseForm  from '../../Client/Components/AdresseForm.vue'

// Libellés des catégories logiques d'intervention
const TYPE_LOGIQUE_LIBELLES = {
    premiere_visite : 'Première visite',
    depannage       : 'Dépannage',
    entretien       : 'Entretien',
    installation    : 'Installation',
    diagnostic      : 'Diagnostic',
    mise_en_service : 'Mise en service',
}

export default {
    name: 'InterventionFormFields',
    components: { ClientForm, AppareilForm, AdresseForm },

    props: {
        value: {
            type   : Object,
            default: () => ({}),
        },

        /**
         * Si true  → affiche la sélection de chantiers existants et appelle l'API
         *            pour les récupérer après le choix d'adresse.
         * Si false → on considère implicitement un nouveau chantier :
         *            chantierSelected passe immédiatement à true,
         *            dataModel.chantier_id reste null, et aucun appel API chantier
         *            n'est effectué.
         */
        withChantiers: {
            type   : Boolean,
            default: false,
        },
    },

    // ─── DONNÉES LOCALES ───────────────────────────────────────────────────────
    data() {
        return {
            dataModel: { ...this.value },

            // Listes de référence
            types       : [],
            techniciens : [],
            statuts     : [],
            appareils   : [],
            chantiers   : [],
            adresses    : [],
            clients     : [],

            // Durée décomposée en heures + minutes (recalculée dans updateDuree)
            dureeHeures  : 0,
            dureeMinutes : 0,
            minutesOptions: Array.from({ length: 60 }, (_, i) => i),

            // Recherche client
            clientSearch    : '',
            showSuggestions : false,
            searchTimeout   : null,

            // Objet client complet sélectionné (pour pré-remplir adresse/téléphone)
            dbClient : null,

            // Filtre actif sur le type logique (ex: "entretien", "depannage"…)
            filterTypeIntervention : '',

            /**
             * Indique si l'étape "chantier" est résolue.
             * - Avec withChantiers : passe à true après l'appel API getChantiersForClient.
             * - Sans withChantiers : passe directement à true lors de la sélection du
             *   client (nouveau chantier implicite).
             */
            chantierSelected : false,

            // Modèles pour les formulaires des modales
            newClient   : {},
            newAppareil : {},
            newAdresse  : {},

            // ─── Flags de chargement ───────────────────────────────────────────
            loadingSearchClient     : false,
            loadingAppareil         : false,
            loadingInterventionTypes: false,
            loadingAddAppareil      : false,
            loadingSaveAdresse      : false,
            loadingCreateClient     : false,
            loadingCreateAppareil   : false,
            loadingCreateAdresse    : false,
            loadingGetData          : false,

            interventionTypeMapping: interventionTypeMappingConfig,

            crenauLabels: {
                'morning'  : 'Matin',
                'afternoon': 'Après-midi',
            },
        }
    },

    filters: {
        momentDate(value) {
            if (!value) return '-'
            return moment(value).format('DD/MM/YYYY')
        },
    },

    // ─── COMPUTED ──────────────────────────────────────────────────────────────
    computed: {
        /** Clients filtrés par la recherche (renvoyés par l'API) */
        filteredClients() {
            return this.clientSearch ? this.clients : []
        },

        /** Catégories logiques uniques à afficher sous forme de tuiles */
        groupedInterventionTypes() {
            const seen   = new Set()
            const groups = []

            for (const type of this.types) {
                if (TYPE_LOGIQUE_LIBELLES[type.type_logique] && !seen.has(type.type_logique)) {
                    seen.add(type.type_logique)
                    groups.push({ key: type.type_logique, libelle: TYPE_LOGIQUE_LIBELLES[type.type_logique] })
                }
            }

            // Sélectionner automatiquement la première catégorie
            if (groups.length && !this.filterTypeIntervention) {
                this.filterTypeIntervention = groups[0].key
            }

            return groups
        },

        /** Types d'intervention correspondant à la catégorie active */
        typesFiltered() {
            if (!this.filterTypeIntervention) return []
            return this.types.filter(t => t.type_logique === this.filterTypeIntervention)
        },

        /** Nom du type sélectionné (pour l'affichage en mode "lecture") */
        selectedTypeName() {
            return this.types.find(t => t.id === this.dataModel.type_id)?.nom ?? ''
        },

        /** Le statut est visible uniquement si durée + type + technicien sont renseignés */
        isStatutVisible() {
            return this.dataModel.duree_estimee > 0
                && this.dataModel.type_id
                && this.dataModel.technicien_id
        },

        /** Technicien sélectionné */
        selectedTechnicien() {
            return this.techniciens.find(t => t.id === this.dataModel.technicien_id)
        },
    },

    // ─── WATCHERS ──────────────────────────────────────────────────────────────
    watch: {
        // Lancement de la recherche client à chaque frappe
        clientSearch() {
            this.searchClients()
        },

        // Synchronisation parent → enfant
        value: {
            deep: true,
            handler(newValue) {
                if (JSON.stringify(newValue) !== JSON.stringify(this.dataModel)) {
                    this.dataModel = { ...newValue }
                }
            },
        },

        // Synchronisation enfant → parent
        dataModel: {
            deep: true,
            handler(newValue) {
                if (JSON.stringify(newValue) !== JSON.stringify(this.value)) {
                    this.$emit('input', { ...newValue })
                }
            },
        },

        // Recalcul de la durée totale à chaque changement des selects
        dureeHeures()  { this.updateDuree() },
        dureeMinutes() { this.updateDuree() },
    },

    // ─── CYCLE DE VIE ──────────────────────────────────────────────────────────
    async mounted() {
        await this.loadData()
    },

    // ─── MÉTHODES ──────────────────────────────────────────────────────────────
    methods: {

        // ── Initialisation ────────────────────────────────────────────────────

        /** Charge les données de référence au montage (statuts, techniciens…) */
        async loadData() {
            this.loadingGetData = true
            this.statuts        = await this.$request.get('/intervention-statuts')
            await this.getTechniciens()
            if (!this.dataModel.statut_id) {
                this.dataModel.statut_id = 10 // Statut par défaut
            }
            if (this.value.technicien_id) {
                this.dataModel.technicien_id = this.value.technicien_id
            }
            this.loadingGetData = false
        },

        async getTechniciens() {
            try {
                const response   = await this.$request.get('/techniciens')
                this.techniciens = response.map(t => ({
                    id   : t.id,
                    name : t.prenom + ' ' + t.nom,
                    role : 'Technicien',
                    color: t.color || '#3b82f6',
                }))
            } catch (e) {
                console.error('Erreur récupération techniciens :', e)
            }
        },

        // ── Recherche & sélection client ──────────────────────────────────────

        /** Recherche de clients avec debounce (700 ms) */
        async searchClients() {
            this.loadingSearchClient = true
            clearTimeout(this.searchTimeout)

            this.searchTimeout = setTimeout(async () => {
                this.clients = []

                if (!this.clientSearch || this.clientSearch.length < 2) {
                    this.loadingSearchClient = false
                    return
                }

                try {
                    const res    = await this.$request.get('/clients', {
                        params: { search: this.clientSearch, with_adresses: 1, type: 'proprietaire' },
                    })
                    this.clients = res.data ?? res
                } catch (e) {
                    console.error('Erreur recherche client', e)
                } finally {
                    this.loadingSearchClient = false
                }
            }, 700)
        },

        /** Sélectionne un client depuis les suggestions et charge ses données */
        async selectClient(client) {
            this.clientSearch                 = `${client.prenom} ${client.nom}`
            this.showSuggestions              = false
            this.dbClient                     = client
            this.dataModel.client_id          = client.id
            this.dataModel.client_appareil_id = null
            this.dataModel.adresse_id         = client.adresses?.length ? client.adresses[0].id : null
            this.adresses                     = client.adresses ?? []

            if (this.withChantiers) {
                // Mode avec gestion des chantiers : on interroge l'API
                await this.getChantiersForClient(client.id)
            } else {
                // Mode sans chantiers : nouveau chantier implicite
                this.chantierSelected      = true
                this.chantiers             = []
                this.dataModel.chantier_id = null
            }

            await this.getAppareilsForClient(client.id)
        },

        /** Ferme les suggestions avec un léger délai (laisse le mousedown se déclencher) */
        hideSuggestionsDelayed() {
            setTimeout(() => (this.showSuggestions = false), 200)
        },

        /** Ouvre la modale de création client en pré-remplissant le nom saisi */
        openNewClientModal() {
            this.newClient = { nom: this.clientSearch }
            this.$modal.show('modal-add-client-intervention')
        },

        // ── Chantiers & adresses ──────────────────────────────────────────────

        /**
         * Handler du @change sur le select adresse.
         * Selon withChantiers, appelle ou non l'API des chantiers.
         */
        onAdresseChange() {
            if (this.withChantiers) {
                this.getChantiersForClient(this.dataModel.client_id)
            }
            // Sans chantiers : chantierSelected reste déjà true, rien à faire
        },

        /** Récupère les chantiers actifs pour le client + adresse sélectionnés */
        async getChantiersForClient(clientId) {
            this.chantierSelected      = false
            this.dataModel.chantier_id = null

            const response        = await this.$request.post('/clients/get-chantiers', {
                client_id : clientId,
                adresse_id: this.dataModel.adresse_id,
                active    : 1,
            })
            this.chantiers        = response ?? []
            this.chantierSelected = true
        },

        /** Ouvre la modale d'ajout d'adresse en pré-remplissant contact + téléphone */
        openNewAdresseModal() {
            this.newAdresse = {
                nom_contact: this.dbClient?.nom,
                telephone  : this.dbClient?.telephone,
            }
            this.$modal.show('modal-add-adresse-intervention')
        },

        // ── Appareils ─────────────────────────────────────────────────────────

        /** Charge les appareils du client ; sélectionne automatiquement si unique */
        async getAppareilsForClient(clientId) {
            this.loadingAppareil = true
            try {
                const res      = await this.$request.get(`/client-appareils?client_id=${clientId}&all=1`)
                this.appareils = res ?? []

                // Sélection automatique si un seul appareil
                if (this.appareils.length === 1) {
                    this.dataModel.client_appareil_id = this.appareils[0].id
                    await this.loadTypesIntervention()
                }
            } catch (e) {
                console.error('Erreur chargement appareils client', e)
                this.appareils = []
            } finally {
                this.loadingAppareil = false
            }
        },

        // ── Types d'intervention ──────────────────────────────────────────────

        /** Charge les types d'intervention selon la catégorie de l'appareil sélectionné */
        async loadTypesIntervention() {
            const appareil = this.appareils.find(a => a.id === this.dataModel.client_appareil_id)
            if (!appareil) return

            this.loadingInterventionTypes = true
            this.types = await this.$request.post('/intervention-types/list', { categorie: appareil.type })
            this.loadingInterventionTypes = false
            this.selectTypeIntervention(this.filterTypeIntervention)
        },

        /** Bascule sur une catégorie logique et sélectionne automatiquement si unique */
        selectTypeIntervention(typeKey) {
            this.filterTypeIntervention = typeKey
            this.dataModel.type_id      = null
            this.$forceUpdate()

            this.$nextTick(() => {
                if (this.typesFiltered.length === 1) {
                    this.selectDureeIntervention(this.typesFiltered[0].id)
                }
            })
        },

        /** Sélectionne un type précis et pré-remplit la durée estimée */
        selectDureeIntervention(typeInterventionId) {
            this.dataModel.type_id = typeInterventionId
            this.$forceUpdate()

            this.$nextTick(() => {
                const selectedType = this.types.find(t => t.id === typeInterventionId)

                if (selectedType?.time_min != null) {
                    const totalMinutes = Number(selectedType.time_min)
                    this.dureeHeures   = Math.floor(totalMinutes / 60)
                    this.dureeMinutes  = totalMinutes % 60
                    this.updateDuree()
                }

                this.$emit('input', { ...this.dataModel })
            })
        },

        // ── Recalcul durée ────────────────────────────────────────────────────

        /** Recalcule dataModel.duree_estimee (en minutes) depuis les selects h/min */
        updateDuree() {
            this.dataModel.duree_estimee = (Number(this.dureeHeures) * 60) + Number(this.dureeMinutes)
            this.$emit('input', { ...this.dataModel })
        },

        // ── Sauvegarde des formulaires modales ────────────────────────────────

        /**
         * Enregistre le propriétaire (donneur d'ordre).
         * Émet le client créé via selectClient().
         */
        async saveClient() {
            this.loadingCreateClient = true
            try {
                const { proprietaire } = this.newClient

                if (!proprietaire) {
                    this.$toast.error('Informations propriétaire manquantes')
                    return
                }

                const type  = proprietaire.type_client || 'particulier'
                const rules = type === 'professionnel'
                    ? [
                        'proprietaire-nom:required|min=2',
                        'proprietaire-siret:required|siret',
                        // 'proprietaire-email:required|email',
                        // 'proprietaire-telephone:required|tel',
                    ]
                    : [
                        'proprietaire-nom:required|min=2',
                        // 'proprietaire-email:required|email',
                        // 'proprietaire-telephone:required|tel',
                    ]

                const validation = this.$inpError(rules)
                if (!validation) {
                    this.$toast.error('Formulaire invalide')
                    this.loadingCreateClient = false
                    return
                }

                const res = await this.$request.post('/clients', {
                    ...proprietaire,
                    type            : 'proprietaire',
                    regie_id        : 0,
                    proprietaire_id : 0,
                })

                const clientPourSelection = { ...res.client, adresses: [] }
                this.$modal.hide('modal-add-client-intervention')
                this.newClient = {}

                await this.selectClient(clientPourSelection)

            } catch (e) {
                console.error(e)
                this.$toast.error("Erreur lors de l'ajout du client")
            } finally {
                this.loadingCreateClient = false
            }
        },

        /** Valide et enregistre un nouvel appareil pour le client courant */
        async saveAppareil() {
            const valid = this.$inpError(['inp-type:required|min=3'])
            if (!valid) { this.$toast.error('Formulaire invalide'); return }

            this.loadingAddAppareil = this.loadingCreateAppareil = true
            try {
                this.newAppareil.client_id = this.dataModel.client_id
                const response = await this.$request.post('/client-appareils', this.newAppareil)
                this.$modal.hide('modal-add-appareil-intervention')
                this.newAppareil = {}
                await this.getAppareilsForClient(this.dataModel.client_id)
                this.dataModel.client_appareil_id = response.appareil.id
                await this.loadTypesIntervention()
                this.$forceUpdate()
            } catch {
                this.$toast.error("Erreur lors de l'enregistrement de l'appareil")
            } finally {
                this.loadingAddAppareil = this.loadingCreateAppareil = false
            }
        },

        /**
         * Enregistre l'adresse du client sélectionné.
         * Si l'adresse est liée à une régie ou un locataire (via newAdresse.regie / newAdresse.locataire),
         * on les crée d'abord et on injecte leurs IDs dans le payload adresse.
         */
        async saveAdresse() {
            this.loadingSaveAdresse = this.loadingCreateAdresse = true
            try {
                const { regie, locataire, ...adressePayload } = this.newAdresse

                const hasRegie     = !!(regie && regie.nom)
                const hasLocataire = !!(locataire && (locataire.nom || locataire.prenom || locataire.email || locataire.telephone))

                const proprietaireType = this.dbClient?.type_client || 'particulier'
                const regieType        = regie?.type_client         || 'particulier'
                const locataireType    = locataire?.type_client     || 'particulier'

                const rules = []

                // Propriétaire
                if (hasRegie) {
                    rules.push('proprietaire-nom:required|min=2')
                } else if (proprietaireType === 'professionnel') {
                    rules.push(
                        'proprietaire-nom:required|min=2',
                        'proprietaire-siret:required|siret',
                    )
                } else {
                    rules.push(
                        'proprietaire-nom:required|min=2',
                        'proprietaire-email:required|email',
                        'proprietaire-telephone:required|tel',
                    )
                }

                // Régie
                if (hasRegie) {
                    if (regieType === 'professionnel') {
                        rules.push(
                            'regie-nom:required|min=2',
                            'regie-siret:required|siret',
                            'regie-email:required|email',
                            'regie-telephone:required|tel'
                        )
                    } else {
                        rules.push(
                            'regie-nom:required|min=2',
                            'regie-email:required|email',
                            'regie-telephone:required|tel',
                        )
                    }
                }

                const validation = this.$inpError(rules)
                if (!validation) {
                    this.$toast.error('Formulaire invalide')
                    this.loadingSaveAdresse = this.loadingCreateAdresse = false
                    return
                }

                if (locataire && !hasLocataire) {
                    this.$toast.error('Veuillez remplir au moins un champ pour le locataire')
                    this.loadingSaveAdresse = this.loadingCreateAdresse = false
                    return
                }

                let regieId     = 0
                let locataireId = 0

                if (regie?.nom) {
                    const res = await this.$request.post('/clients', {
                        ...regie,
                        type            : 'regie',
                        regie_id        : 0,
                        proprietaire_id : 0,
                    })
                    regieId = res.client.id
                }

                if (locataire?.nom) {
                    const res = await this.$request.post('/clients', {
                        ...locataire,
                        type            : 'locataire',
                        regie_id        : regieId,
                        proprietaire_id : this.dataModel.client_id,
                    })
                    locataireId = res.client.id
                }

                const response = await this.$request.post(
                    `/clients/${this.dataModel.client_id}/adresses`,
                    {
                        ...adressePayload,
                        client_id    : this.dataModel.client_id,
                        regie_id     : regieId,
                        locataire_id : locataireId,
                    }
                )

                this.$modal.hide('modal-add-adresse-intervention')
                this.newAdresse = {}
                this.adresses.push(response)
                this.dataModel.adresse_id = response.id

            } catch (e) {
                console.error(e)
                this.$toast.error("Erreur lors de l'ajout de l'adresse")
            } finally {
                this.loadingSaveAdresse = this.loadingCreateAdresse = false
            }
        },
    },
}
</script>

<style scoped>
/* Scrollbar personnalisée dans les listes déroulantes */
ul::-webkit-scrollbar       { width: 6px; }
ul::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 3px; }
</style>